import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  Badge,
  IconButton
} from '@chakra-ui/react';
import { FiArrowLeft, FiBook, FiPlay } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface ComicData {
  id: string;
  title: string;
  author: string;
  description: string;
  thumbnail: string;
  rating: number;
  year: number;
  genres: string[];
  language: string;
  uploader: string;
  uploaderId: string;
  uploaderAvatar?: string;
  views: number;
  likes: number;
  dislikes: number;
  tags: string[];
  category: string;
  assets?: {
    bookFile?: string;
    coverFile?: string;
    volumes?: Array<{
      title?: string;
      description?: string;
      volumeNumber?: number;
      pages?: number;
      coverUrl?: string;
      fileUrl?: string;
    }>;
  };
  createdAt: any;
  status: string;
  isPublic: boolean;
}

const ComicVolumes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [comic, setComic] = useState<ComicData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const textColor = useColorModeValue('gray.900', 'white');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('purple.500', 'purple.400');

  useEffect(() => {
    const fetchComicData = async () => {
      if (!id) {
        setError('Comic ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const comicDoc = await getDoc(doc(db, 'content', id));
        
        if (comicDoc.exists()) {
          const comicData = { id: comicDoc.id, ...comicDoc.data() } as ComicData;
          
          if (comicData.category !== 'comic') {
            setError('Content not found');
            return;
          }
          
          setComic(comicData);
        } else {
          setError('Comic not found');
        }
      } catch (err) {
        console.error('Error fetching comic:', err);
        setError('Failed to load comic');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComicData();
  }, [id]);

  const handleVolumeRead = (volume: any) => {
    if (!volume.fileUrl) {
      toast({
        title: 'Volume not available',
        description: 'This volume is not available for reading.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Navigate to reader with volume data and comic ID
    navigate(`/comic/${id}/read`, { 
      state: { 
        volume, 
        comicTitle: comic?.title,
        volumeTitle: volume.title || `Volume ${volume.volumeNumber}`,
        comicId: id // Pass the actual comic document ID
      } 
    });
  };

  if (isLoading) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <Center py={20}>
          <VStack gap={4}>
            <Spinner size="xl" color="purple.500" />
            <Text color={labelColor}>Loading comic volumes...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (error || !comic) {
    return (
      <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
        <Center py={20}>
          <VStack gap={4}>
            <Text color="red.500" fontSize="lg">{error}</Text>
            <Button colorScheme="purple" onClick={() => navigate(`/comic/${id}`)}>
              Back to Comic
            </Button>
          </VStack>
        </Center>
      </Box>
    );
  }

  const volumes = comic.assets?.volumes || [];

  return (
    <Box pt={{ base: '80px', md: '90px' }} px={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="start" wrap="wrap" gap={4}>
          <VStack align="start" gap={2}>
            <HStack gap={3}>
              <IconButton
                aria-label="Back to comic"
                icon={<IconWrapper icon={FiArrowLeft} size={20} />}
                variant="ghost"
                onClick={() => navigate(`/comic/${id}`)}
              />
              <IconWrapper icon={FiBook} size={24} color="purple.500" />
              <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={textColor}>
                {comic.title}
              </Text>
            </HStack>
            <Text color={labelColor} fontSize={{ base: 'sm', md: 'md' }}>
              Select a volume to start reading
            </Text>
          </VStack>
        </HStack>

        {/* Volumes Grid */}
        {volumes.length === 0 ? (
          <Center py={20}>
            <VStack gap={4}>
              <IconWrapper icon={FiBook} size={48} color="gray.400" />
              <Text color={labelColor} fontSize="lg">No volumes available</Text>
              <Text color={labelColor} fontSize="sm" textAlign="center">
                This comic doesn't have any volumes uploaded yet.
              </Text>
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={6}>
            {volumes.map((volume, index) => (
              <Box
                key={index}
                bg={cardBg}
                borderRadius="lg"
                overflow="hidden"
                shadow="sm"
                _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
                border="1px solid"
                borderColor={borderColor}
              >
                {/* Volume Cover */}
                <Box position="relative" w="100%" h="200px">
                  {(() => {
                    // Try multiple possible cover URL fields
                    const coverUrl = volume.coverUrl || (volume as any).cover || (volume as any).poster || (volume as any).thumbnail;
                    
                    return coverUrl ? (
                      <CachedImage
                        src={coverUrl}
                        alt={volume.title || `Volume ${volume.volumeNumber || index + 1}`}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                    ) : (
                      <Box
                        w="100%"
                        h="100%"
                        bg="gray.100"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <IconWrapper icon={FiBook} size={48} color="gray.400" />
                      </Box>
                    );
                  })()}
                  
                  {/* Volume Number Badge */}
                  <Badge
                    position="absolute"
                    top={2}
                    right={2}
                    colorScheme="purple"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    Vol. {volume.volumeNumber || index + 1}
                  </Badge>
                </Box>
                
                {/* Volume Info */}
                <VStack align="start" p={4} gap={3}>
                  <VStack align="start" gap={1} w="100%">
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={textColor}
                      noOfLines={2}
                      lineHeight="short"
                    >
                      {volume.title || `Volume ${volume.volumeNumber || index + 1}`}
                    </Text>
                    
                    {volume.description && (
                      <Text fontSize="sm" color={labelColor} noOfLines={2}>
                        {volume.description}
                      </Text>
                    )}
                    
                    <HStack justify="space-between" w="100%">
                      {volume.pages && (
                        <Text fontSize="xs" color={labelColor}>
                          {volume.pages} pages
                        </Text>
                      )}
                      <Text fontSize="xs" color={labelColor}>
                        {volume.fileUrl ? 'Available' : 'Not Available'}
                      </Text>
                    </HStack>
                  </VStack>
                  
                  {/* Read Button */}
                  <Button
                    size="sm"
                    colorScheme="purple"
                    w="100%"
                    leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                    isDisabled={!volume.fileUrl}
                    onClick={() => handleVolumeRead(volume)}
                  >
                    {volume.fileUrl ? 'Read Volume' : 'Not Available'}
                  </Button>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </VStack>
    </Box>
  );
};

export default ComicVolumes;
